// Per docs/conventions.md: every API call goes through src/api/. Components consume
// TanStack Query hooks, not raw fetch.
//
// Tier A: hardcoded shapes the saas-backend returns. Replaced once the OpenAPI
// generator is wired (Phase 7).

const DEFAULT_BASE_URL =
  import.meta.env.VITE_API_BASE_URL ?? "http://localhost:8000/api/v1";

async function get<T>(path: string): Promise<T> {
  const res = await fetch(`${DEFAULT_BASE_URL}${path}`, {
    headers: { Accept: "application/json" },
  });
  if (!res.ok) {
    throw new Error(`${res.status} ${res.statusText} from GET ${path}`);
  }
  return (await res.json()) as T;
}

export interface Score {
  letter: string;
  value: number;
  delta_30d?: number;
  computed_at?: string;
}

export interface Household {
  id: string;
  display_name: string;
  subscription: { tier: string; status: string; renews_at: string };
  current_score: Score;
  consent: { version: string; accepted_at: string };
}

export interface Device {
  id: string;
  display_name: string;
  kind: string;
  mac: string;
  ip: string;
  tier: "robust" | "fragile";
  last_seen: string;
  is_shim_host: boolean;
}

export interface Finding {
  id: string;
  severity: "low" | "medium" | "high" | "critical";
  kill_chain_stage: string;
  title: string;
  plain_summary: string;
  device_id: string;
  detected_at: string;
  status: "open" | "acknowledged" | "resolved";
}

export interface Report {
  id: string;
  scan_id: string;
  started_at: string;
  completed_at: string;
  score: Score;
  findings_summary: { high: number; medium: number; low: number };
  kind: "cold_start" | "weekly";
}

export interface LiveScanEvent {
  ts: string;
  stage: string;
  kind: "win" | "info" | "finding";
  headline: string;
}

export interface LiveScan {
  scan_id: string;
  status: "running" | "halted" | "complete";
  stage: string;
  stage_index: number;
  stage_count: number;
  started_at: string;
  estimated_completion_at: string;
  active_modules: string[];
  wins_today: number;
  events: LiveScanEvent[];
}

export interface HaltState {
  halted: boolean;
  halt_url: string;
  halt_code: string;
  last_halt_at: string | null;
  explanation: string;
}

export type RibbonOutcome =
  | "caught"
  | "finding_low"
  | "finding_medium"
  | "finding_high";

export interface ScanFlowScenario {
  id: string;
  name: string;
}

export interface ScanFlowRibbon {
  scenario: string;
  device: string;
  attempts: number;
  outcome: RibbonOutcome;
  finding_id?: string;
}

export interface ScanFlow {
  scan_id: string;
  started_at: string;
  scenarios: ScanFlowScenario[];
  ribbons: ScanFlowRibbon[];
}

export interface NarrativeOption {
  id: string;
  label: string;
}

export interface ReportNarrative {
  scope_options: string[];
  window_options: NarrativeOption[];
  lens_options: NarrativeOption[];
  totals: Record<
    string,
    {
      scenarios_run: number;
      devices_in_scope: number;
      high_count: number;
      medium_count: number;
      low_count: number;
      caught_count: number;
      score_letter: string;
      score_value: number;
      trend_direction: "up" | "down" | "flat";
      trend_delta: number;
    }
  >;
}

export interface Notification {
  id: string;
  type: string;
  title: string;
  plain_summary: string;
  severity: "low" | "medium" | "high" | "critical" | null;
  device_name: string | null;
  created_at: string;
  status: string;
}

export const api = {
  account: () => get<Household>("/account"),
  devices: () => get<{ devices: Device[] }>("/devices"),
  findings: () => get<{ findings: Finding[] }>("/findings"),
  reports: () => get<{ reports: Report[] }>("/reports"),
  liveScan: () => get<LiveScan>("/scans/live"),
  scanFlow: () => get<ScanFlow>("/scans/flow"),
  halt: () => get<HaltState>("/halt"),
  reportNarrative: () => get<ReportNarrative>("/reports/narrative"),
  notifications: () => get<{ notifications: Notification[] }>("/notifications"),
};
